import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Logo-only Junebase splash
// 1) Centered logo fades in
// 2) Holds briefly
// 3) Overlay fades out
// 4) Content appears
export default function JunebaseSplash({
  logoSrc = "/logo.png",
  overlayBg = "#000000",
  runOncePerSession = true,
  // timings (ms)
  fadeLogoMs = 1200,
  holdLogoMs = 400,
  fadeOutMs = 500,
  onDone,
  children,
}) {
  const sessionKey = useMemo(() => `jb_intro_seen_logo_only_${logoSrc}`, [logoSrc]);

  const [showIntro, setShowIntro] = useState(() => {
    if (typeof window === "undefined") return true;

    // If you truly want "once per session", this is enough:
    if (window.__jbSplashShown) return false;
    window.__jbSplashShown = true;

    // Optional: keep this if you want "once per tab sessionStorage"
    if (runOncePerSession && sessionStorage.getItem(sessionKey) === "1") return false;

    return true;
  });

  const [stage, setStage] = useState(showIntro ? "logo" : "done"); // "logo" -> "done"

  useEffect(() => {
    if (!showIntro) return;

    const doneAt = fadeLogoMs + holdLogoMs + fadeOutMs;

    const t = setTimeout(() => {
      setStage("done");
      if (runOncePerSession) sessionStorage.setItem(sessionKey, "1");
      if (onDone) onDone();
    }, doneAt);

    return () => clearTimeout(t);
  }, [showIntro, fadeLogoMs, holdLogoMs, fadeOutMs, runOncePerSession, sessionKey]);

  return (
    <div style={{ minHeight: "100vh" }}>
      {/* Content */}
      <div style={{ minHeight: "100vh" }}>{stage === "done" ? children : null}</div>

      {/* Intro overlay */}
      <AnimatePresence>
        {stage !== "done" && (
          <motion.div
            key="overlay"
            initial={{ opacity: 1 }}
            animate={{ opacity: stage === "logo" ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: fadeOutMs / 1000, ease: "easeOut" }}
            style={{
              position: "fixed",
              inset: 0,
              background: overlayBg,
              display: "grid",
              placeItems: "center",
              zIndex: 1000,
            }}
          >
            <motion.img
              src={logoSrc}
              alt="Junebase logo"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: fadeLogoMs / 1000, ease: "easeOut" }}
              style={{ width: 260, height: "auto" }} // bump this up if you want bigger
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}